import { WidgetsMap } from 'types/infographTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { BasicShapeWidgetData } from 'widgets/BasicShapeWidget/BasicShapeWidget.types';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData } from 'widgets/Widget.types';

import { WidgetComparisonStrategy } from './WidgetComparisonStrategy.types';

/**
 * Default strategy for comparing widgets.
 * Always returns true, implying all widgets are similar by default.
 *
 * @class
 * @implements {WidgetComparisonStrategy}
 */
class DefaultWidgetComparisonStrategy implements WidgetComparisonStrategy {
  /**
   * Determines if two widgets are similar.
   *
   * @returns {boolean} - Always returns true.
   */
  isSimilar(): boolean {
    // Generic comparison logic for all widgets
    return true;
  }
}

/**
 * Strategy for comparing shape widgets.
 *
 * @class
 * @implements {WidgetComparisonStrategy}
 */
class ShapeWidgetComparisonStrategy implements WidgetComparisonStrategy {
  /**
   * Compares two BasicShapeWidgetData objects.
   *
   * @param {BasicShapeWidgetData} widget1 - The first shape widget data.
   * @param {BasicShapeWidgetData} widget2 - The second shape widget data.
   * @returns {boolean} - True if widgets have the same shape type and fill color, false otherwise.
   */
  isSimilar(widget1: BasicShapeWidgetData, widget2: BasicShapeWidgetData): boolean {
    // Check if the BasicShapeType, fill color array are the same
    return (
      widget1.type === widget2.type &&
      widget1.fillColor.length === widget2.fillColor.length &&
      widget1.border.color === widget2.border.color &&
      widget1.border.style === widget2.border.style &&
      widget1.border.width === widget2.border.width &&
      widget1.fillColor.every((color, index) => color === widget2.fillColor[index])
    );
  }
}

/**
 * Strategy for comparing group widgets.
 *
 * @class
 * @implements {WidgetComparisonStrategy}
 */
class GroupWidgetComparisonStrategy implements WidgetComparisonStrategy {
  /**
   * Compares two GroupWidgetData objects.
   *
   * @param {GroupWidgetData} widget1 - The first group widget data.
   * @param {GroupWidgetData} widget2 - The second group widget data.
   * @param {WidgetsMap} [widgets] - Optional map of widgets for additional context.
   * @returns {boolean} - True if group widgets have the same number and type of children, false otherwise.
   */
  isSimilar(widget1: GroupWidgetData, widget2: GroupWidgetData, widgets?: WidgetsMap): boolean {
    // Check if the group widget has the same number of children
    if (widget1.memberWidgetIds.length !== widget2.memberWidgetIds.length) {
      return false;
    }

    const shapeComparisonStrategy = new ShapeWidgetComparisonStrategy();

    for (let i = 0; i < widget1.memberWidgetIds.length; i++) {
      const childWidget1Id = widget1.memberWidgetIds[i];
      const childWidget2Id = widget2.memberWidgetIds[i];

      const childWidget1 = widgets?.[childWidget1Id];
      const childWidget2 = widgets?.[childWidget2Id];

      if (!childWidget1 || !childWidget2) {
        return false;
      }

      // Check if both widgets are of the same type
      if (getWidgetTypeFromId(childWidget1Id) !== getWidgetTypeFromId(childWidget2Id)) {
        return false;
      }

      // Check if both widgets are of the BasicShape type
      if (
        getWidgetTypeFromId(childWidget1Id) !== WidgetType.BasicShape ||
        getWidgetTypeFromId(childWidget2Id) !== WidgetType.BasicShape
      ) {
        continue;
      }

      // Use ShapeWidgetComparisonStrategy to compare each pair of child widgets
      if (
        !shapeComparisonStrategy.isSimilar(childWidget1 as BasicShapeWidgetData, childWidget2 as BasicShapeWidgetData)
      ) {
        return false;
      }
    }

    return true;
  }
}

/**
 * Retrieves the appropriate widget comparison strategy based on the widget type.
 *
 * @param {WidgetType} widgetType - The type of the widget.
 * @returns {WidgetComparisonStrategy} - The strategy for comparing widgets of the given type.
 */
const getComparisonStrategy = (widgetType: WidgetType): WidgetComparisonStrategy => {
  // Define a mapping from WidgetType to strategy
  const strategyMapping: { [key in WidgetType]?: WidgetComparisonStrategy } = {
    [WidgetType.BasicShape]: new ShapeWidgetComparisonStrategy(),
    [WidgetType.Group]: new GroupWidgetComparisonStrategy(),
  };

  const strategy = strategyMapping[widgetType];
  if (!strategy) {
    return new DefaultWidgetComparisonStrategy();
  }

  return strategy;
};

/**
 * Determines if two widgets, identified by their IDs and data, are similar.
 *
 * @param {[string, Widget | AllWidgetData]} widget1 - Tuple of widget ID and data for the first widget.
 * @param {[string, Widget | AllWidgetData]} widget2 - Tuple of widget ID and data for the second widget.
 * @param {WidgetsMap} widgets - Map of widget IDs to widget data.
 * @returns {boolean} - True if the widgets are considered similar, false otherwise.
 */
export const isWidgetsSimilar = (
  [widgetId1, data1]: [string, Widget | AllWidgetData],
  [widgetId2, data2]: [string, Widget | AllWidgetData],
  widgets: WidgetsMap,
): boolean => {
  if (getWidgetTypeFromId(widgetId1) !== getWidgetTypeFromId(widgetId2)) {
    return false;
  }

  const strategy = getComparisonStrategy(getWidgetTypeFromId(widgetId1));
  return strategy.isSimilar(data1, data2, widgets);
};
